import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Input, Modal, Form, Select, Typography,
  Space, Alert, Empty, Upload, message, Popconfirm, Tag, Row, Col, Statistic, Segmented, theme as antdTheme,
} from 'antd';
import {
  PlusOutlined, SearchOutlined, UploadOutlined, UserOutlined,
  DeleteOutlined, DownloadOutlined, EditOutlined,
  TeamOutlined, RiseOutlined, UnorderedListOutlined, StopOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const LIST_COLORS = ['#6366f1', '#9DC533', '#f59e0b', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6'];
const listColor = (id) => LIST_COLORS[(id - 1) % LIST_COLORS.length];

const Subscribers = () => {
  const { token: themeToken } = antdTheme.useToken();
  const [subscribers, setSubscribers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  const [lists, setLists] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterListId, setFilterListId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Add
  const [addModal, setAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addForm] = Form.useForm();

  // Edit
  const [editModal, setEditModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [editForm] = Form.useForm();

  // Import
  const [importModal,    setImportModal]    = useState(false);
  const [csvFile,        setCsvFile]        = useState(null);
  const [csvRowCount,    setCsvRowCount]    = useState(null);
  const [importListId,   setImportListId]   = useState('');
  const [importListMode, setImportListMode] = useState('existing'); // 'existing' | 'new'
  const [newListName,    setNewListName]    = useState('');
  const [importing,      setImporting]      = useState(false);
  const [importStep,     setImportStep]     = useState(1);   // 1=upload 2=map 3=result
  const [previewing,     setPreviewing]     = useState(false);
  const [csvHeaders,     setCsvHeaders]     = useState([]);
  const [csvSampleRows,  setCsvSampleRows]  = useState([]);
  const [columnMapping,  setColumnMapping]  = useState({});
  const [dbFields,       setDbFields]       = useState([]);
  const [importResult,   setImportResult]   = useState(null);


  const fetchAll = async (p = page) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: p, limit: PAGE_SIZE };
      if (filterListId) params.list_id = filterListId;
      if (filterStatus) params.status = filterStatus;
      if (search.trim()) params.search = search.trim();
      const [s, l, st] = await Promise.all([
        axios.get('/api/subscribers', { params }),
        axios.get('/api/lists'),
        axios.get('/api/subscribers/stats'),
      ]);
      setSubscribers(s.data.data);
      setTotal(s.data.total);
      setLists(l.data);
      setStats(st.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(1); setPage(1); }, [filterListId, filterStatus]);
  useEffect(() => { fetchAll(page); }, [page]);
  // Debounced server-side search — resets to page 1 and queries the whole table
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchAll(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── Add ──────────────────────────────────────────────────────────────
  const handleAdd = async (values) => {
    setSaving(true);
    try {
      await axios.post('/api/subscribers', values);
      addForm.resetFields();
      setAddModal(false);
      message.success('Subscriber added');
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add subscriber');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────
  const openEdit = (record) => {
    setEditingSubscriber(record);
    // Support legacy records that only have 'name'
    let first = record.first_name || '';
    let last  = record.last_name  || '';
    if (!first && !last && record.name) {
      const parts = record.name.trim().split(/\s+/);
      first = parts[0] || '';
      last  = parts.slice(1).join(' ') || '';
    }
    editForm.setFieldsValue({
      first_name: first,
      last_name:  last,
      email:      record.email   || '',
      phone:      record.phone   || '',
      city:       record.city    || '',
      state:      record.state   || '',
      country:    record.country || '',
    });
    setEditModal(true);
  };

  const handleUpdate = async (values) => {
    setUpdating(true);
    try {
      await axios.put(`/api/subscribers/${editingSubscriber.id}`, values);
      message.success('Subscriber updated');
      setEditModal(false);
      setEditingSubscriber(null);
      editForm.resetFields();
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update subscriber');
    } finally {
      setUpdating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/subscribers/${id}`);
      message.success('Subscriber deleted');
      setSelectedRowKeys(prev => prev.filter(k => k !== id));
      fetchAll();
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete subscriber');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.post('/api/subscribers/bulk-delete', { ids: selectedRowKeys });
      message.success(`${selectedRowKeys.length} subscriber(s) deleted`);
      setSelectedRowKeys([]);
      fetchAll();
    } catch (err) {
      message.error(err.response?.data?.message || 'Bulk delete failed');
      fetchAll();
    }
  };


  // ── CSV template download ─────────────────────────────────────────────
  const downloadCsvTemplate = () => {
    const rows = [
      'first_name,last_name,email,phone,city,state,country',
      'John,Doe,john.doe@example.com,+1 555 0001,New York,NY,United States',
      'Jane,Smith,jane.smith@example.com,+1 555 0002,Los Angeles,CA,United States',
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'subscribers_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Import ────────────────────────────────────────────────────────────
  const resetImportModal = () => {
    setCsvFile(null);
    setCsvRowCount(null);
    setImportListId('');
    setNewListName('');
    setImportListMode('existing');
    setImportModal(false);
    setImportStep(1);
    setPreviewing(false);
    setCsvHeaders([]);
    setCsvSampleRows([]);
    setColumnMapping({});
    setDbFields([]);
    setImportResult(null);
  };

  const handleCsvSelect = (file) => {
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const lines = e.target.result.split('\n').filter(l => l.trim() !== '');
      setCsvRowCount(Math.max(0, lines.length - 1));
    };
    reader.readAsText(file);
    return false;
  };

  // Step 1 → Step 2: upload CSV for preview, get suggested mapping
  const handlePreview = async () => {
    if (!csvFile) { message.warning('Please select a CSV file'); return; }
    if (importListMode === 'existing' && !importListId) { message.warning('Please select a list'); return; }
    if (importListMode === 'new' && !newListName.trim()) { message.warning('Please enter a name for the new list'); return; }
    setPreviewing(true);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const { data } = await axios.post('/api/subscribers/import/preview', fd);
      setCsvHeaders(data.headers);
      setCsvSampleRows(data.sampleRows);
      setDbFields(data.fields);
      // Pre-fill with suggested mapping; null means unmapped
      const suggested = {};
      data.headers.forEach(h => { suggested[h] = data.suggestedMapping[h] || null; });
      setColumnMapping(suggested);
      setImportStep(2);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to preview CSV');
    } finally {
      setPreviewing(false);
    }
  };

  // Step 2 → Step 3: actual import with mapping
  const handleImport = async () => {
    const emailMapped = Object.values(columnMapping).some(v => v === 'email');
    if (!emailMapped) { message.warning('You must map at least one column to Email'); return; }
    setImporting(true);
    try {
      let listId = importListId;
      if (importListMode === 'new') {
        const res = await axios.post('/api/lists', { name: newListName.trim() });
        listId = res.data.id;
      }
      // Replace null mappings with __skip__
      const finalMapping = {};
      Object.entries(columnMapping).forEach(([col, field]) => {
        finalMapping[col] = field || '__skip__';
      });
      const fd = new FormData();
      fd.append('file', csvFile);
      fd.append('list_id', listId);
      fd.append('mapping', JSON.stringify(finalMapping));
      const { data: result } = await axios.post('/api/subscribers/import', fd);
      setImportResult(result);
      setImportStep(3);
      fetchAll();
    } catch (err) {
      message.error(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Search, list, and status filtering are all applied server-side (across the
  // whole table, not just the current page), so render the rows as returned.
  const filtered = subscribers;

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Subscriber', key: 'subscriber',
      render: (_, record) => {
        const displayName = [record.first_name, record.last_name].filter(Boolean).join(' ') || record.name || '';
        return (
          <Space size={10}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Text style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                {(displayName || record.email || '?')[0].toUpperCase()}
              </Text>
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: themeToken.colorText }}>{displayName || '—'}</div>
              <div style={{ color: themeToken.colorTextSecondary, fontSize: 12 }}>{record.email}</div>
            </div>
          </Space>
        );
      },
    },
    { title: 'Phone', dataIndex: 'phone', render: v => v || '—', responsive: ['md'] },
    {
      title: 'Location', key: 'location', responsive: ['lg'],
      render: (_, r) => [r.city, r.state, r.country].filter(Boolean).join(', ') || '—',
    },
    {
      title: 'Status', key: 'status', width: 130,
      render: (_, r) => {
        const cfg = {
          active:       { color: 'success', label: 'Active' },
          bounced:      { color: 'warning', label: 'Bounced' },
          complaint:    { color: 'error',   label: 'Complained' },
          unsubscribed: { color: 'default', label: 'Unsubscribed' },
        }[r.status] || { color: 'default', label: r.status || '—' };
        return <Tag color={cfg.color} style={{ borderRadius: 6, fontWeight: 500, fontSize: 11 }}>{cfg.label}</Tag>;
      },
    },
    {
      title: 'Lists', key: 'lists',
      render: (_, record) => (
        <Space size={4} wrap>
          {(record.lists || []).length === 0
            ? <Text style={{ color: '#9ca3af', fontSize: 12 }}>—</Text>
            : (record.lists || []).map(l => (
                <Tag
                  key={l.id}
                  style={{
                    background: listColor(l.id) + '18',
                    color: listColor(l.id),
                    border: `1px solid ${listColor(l.id)}40`,
                    borderRadius: 6,
                    fontSize: 11,
                    fontWeight: 500,
                    padding: '1px 8px',
                  }}
                >
                  {l.name}
                </Tag>
              ))
          }
        </Space>
      ),
    },
    {
      title: 'Added', dataIndex: 'created_at', responsive: ['md'],
      render: d => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    },
    {
      title: '', align: 'right', width: 90,
      render: (_, record) => (
        <Space size={4}>
          <Button
            icon={<EditOutlined />} size="small" type="text"
            style={{ color: '#6366f1' }}
            onClick={() => openEdit(record)}
          />
          <Popconfirm
            title="Delete this subscriber?"
            description="This will remove them from all lists."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small" type="text" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Shared subscriber fields ───────────────────────────────────────────
  const SubscriberFields = () => (
    <>
      <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
        <Input placeholder="subscriber@example.com" size="large" style={{ borderRadius: 10 }} />
      </Form.Item>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Form.Item name="first_name" label="First Name" style={{ marginBottom: 12 }}>
          <Input placeholder="John" style={{ borderRadius: 10 }} />
        </Form.Item>
        <Form.Item name="last_name" label="Last Name" style={{ marginBottom: 12 }}>
          <Input placeholder="Doe" style={{ borderRadius: 10 }} />
        </Form.Item>
        <Form.Item name="phone" label="Phone"><Input placeholder="+1 555 0000" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="city" label="City"><Input placeholder="New York" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="state" label="State"><Input placeholder="NY" style={{ borderRadius: 10 }} /></Form.Item>
        <Form.Item name="country" label="Country"><Input placeholder="United States" style={{ borderRadius: 10 }} /></Form.Item>
      </div>
    </>
  );

  const statCardStyle = {
    borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)', height: '100%',
  };

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Subscribers</Title>
          <Text style={{ color: '#6b7280' }}>{total} subscriber{total !== 1 ? 's' : ''} total</Text>
        </div>
        <Space wrap>
          <Button icon={<UploadOutlined />} onClick={() => setImportModal(true)} style={{ borderRadius: 10 }}>Import CSV</Button>
          <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setAddModal(true)} style={{ borderRadius: 10, fontWeight: 600 }}>
            Add Subscriber
          </Button>
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 10 }} />}

      {/* ── Stats Cards ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} lg={4}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: '#6b7280', fontSize: 13 }}>Total Subscribers</Text>}
              value={stats?.total ?? '—'}
              prefix={<TeamOutlined style={{ color: '#6366f1', marginRight: 4 }} />}
              valueStyle={{ color: '#111827', fontSize: 26, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: '#6b7280', fontSize: 13 }}>Active Subscribers</Text>}
              value={stats?.active ?? '—'}
              prefix={<TeamOutlined style={{ color: '#9DC533', marginRight: 4 }} />}
              valueStyle={{ color: '#9DC533', fontSize: 26, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: '#6b7280', fontSize: 13 }}>New This Month</Text>}
              value={stats?.newThisMonth ?? '—'}
              prefix={<RiseOutlined style={{ color: '#9DC533', marginRight: 4 }} />}
              valueStyle={{ color: '#9DC533', fontSize: 26, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: '#6b7280', fontSize: 13 }}>Mailing Lists</Text>}
              value={stats?.listCount ?? '—'}
              prefix={<UnorderedListOutlined style={{ color: '#3b82f6', marginRight: 4 }} />}
              valueStyle={{ color: '#111827', fontSize: 26, fontWeight: 700 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} lg={4}>
          <Card style={statCardStyle} styles={{ body: { padding: '20px 24px' } }}>
            <Statistic
              title={<Text style={{ color: '#6b7280', fontSize: 13 }}>Suppressed</Text>}
              value={stats?.suppressedCount ?? '—'}
              prefix={<StopOutlined style={{ color: '#ef4444', marginRight: 4 }} />}
              valueStyle={{ color: stats?.suppressedCount > 0 ? '#ef4444' : '#111827', fontSize: 26, fontWeight: 700 }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Table Card ── */}
      <Card style={{ borderRadius: 14, border: `1px solid ${themeToken.colorBorderSecondary}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            placeholder="Search by name or email…"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: '1 1 180px', minWidth: 160, maxWidth: 280, borderRadius: 10 }}
            allowClear
          />
          <Select
            placeholder="All Lists"
            allowClear
            value={filterListId}
            onChange={v => setFilterListId(v ?? null)}
            style={{ minWidth: 160, borderRadius: 10 }}
          >
            {lists.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
          </Select>
          <Select
            placeholder="All Statuses"
            allowClear
            value={filterStatus}
            onChange={v => setFilterStatus(v ?? null)}
            style={{ minWidth: 150, borderRadius: 10 }}
          >
            <Option value="active">Active</Option>
            <Option value="bounced">Bounced</Option>
            <Option value="complaint">Complained</Option>
            <Option value="unsubscribed">Unsubscribed</Option>
          </Select>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={`Delete ${selectedRowKeys.length} subscriber(s)?`}
              description="This cannot be undone."
              onConfirm={handleBulkDelete}
              okText="Delete All" okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} style={{ borderRadius: 8 }}>
                Delete Selected ({selectedRowKeys.length})
              </Button>
            </Popconfirm>
          )}
        </div>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          loading={loading}
          rowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subscribers yet" /> }}
          pagination={{
            current: page, pageSize: PAGE_SIZE, total,
            onChange: (p) => setPage(p),
            showTotal: t => `${t} subscriber${t !== 1 ? 's' : ''}`,
            showSizeChanger: false,
          }}
          scroll={{ x: 480 }}
        />
      </Card>

      {/* ── Add Subscriber Modal ── */}
      <Modal
        title={<Space><div style={{ width: 32, height: 32, background: '#EEF6D0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><UserOutlined style={{ color: '#9DC533' }} /></div><span>Add Subscriber</span></Space>}
        open={addModal}
        onCancel={() => { setAddModal(false); addForm.resetFields(); }}
        footer={null} destroyOnHidden width={520}
      >
        <Form form={addForm} onFinish={handleAdd} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="list_id" label="Mailing List" rules={[{ required: true, message: 'Select a list' }]}>
            <Select placeholder="Select a list" size="large">
              {lists.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
            </Select>
          </Form.Item>
          <SubscriberFields />
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setAddModal(false); addForm.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} style={{ borderRadius: 8 }}>Add Subscriber</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Edit Subscriber Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: '#ede9fe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EditOutlined style={{ color: '#6366f1' }} />
            </div>
            <span>Edit Subscriber</span>
          </Space>
        }
        open={editModal}
        onCancel={() => { setEditModal(false); setEditingSubscriber(null); editForm.resetFields(); }}
        footer={null} destroyOnHidden width={520}
      >
        {editingSubscriber && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: themeToken.colorBgLayout, borderRadius: 10, border: `1px solid ${themeToken.colorBorderSecondary}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Text style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
                {([editingSubscriber.first_name, editingSubscriber.last_name].filter(Boolean).join(' ') || editingSubscriber.name || editingSubscriber.email || '?')[0].toUpperCase()}
              </Text>
            </div>
            <div>
              <Text strong style={{ fontSize: 13 }}>{editingSubscriber.name || editingSubscriber.email}</Text>
              <div style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>
                {(editingSubscriber.lists || []).map(l => (
                  <Tag key={l.id} style={{ background: listColor(l.id) + '18', color: listColor(l.id), border: `1px solid ${listColor(l.id)}40`, borderRadius: 6, fontSize: 11, marginRight: 4 }}>{l.name}</Tag>
                ))}
              </div>
            </div>
          </div>
        )}
        <Form form={editForm} onFinish={handleUpdate} layout="vertical">
          <SubscriberFields />
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setEditModal(false); setEditingSubscriber(null); editForm.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={updating}
                style={{ borderRadius: 8, background: '#6366f1', borderColor: '#6366f1' }}>
                Save Changes
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Import CSV Modal ── */}
      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: '#dbeafe', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <UploadOutlined style={{ color: '#3b82f6' }} />
            </div>
            <span>
              {importStep === 1 ? 'Import from CSV' : importStep === 2 ? 'Map CSV Columns' : 'Import Complete'}
            </span>
          </Space>
        }
        open={importModal}
        onCancel={resetImportModal}
        footer={
          importStep === 1 ? (
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button onClick={resetImportModal} style={{ borderRadius: 8 }}>Cancel</Button>
              <Button
                type="primary"
                onClick={handlePreview}
                loading={previewing}
                disabled={!csvFile}
                style={{ borderRadius: 8 }}
              >
                Continue →
              </Button>
            </Space>
          ) : importStep === 2 ? (() => {
            const mapped = Object.values(columnMapping).filter(v => v && v !== '__skip__').length;
            const ignored = csvHeaders.length - mapped;
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 12, color: '#6b7280' }}>
                  To import <strong>{mapped}</strong> column{mapped !== 1 ? 's' : ''} &nbsp;·&nbsp; Ignored <strong>{ignored}</strong>
                </Text>
                <Space>
                  <Button onClick={() => setImportStep(1)} style={{ borderRadius: 8 }}>← Back</Button>
                  <Button
                    onClick={() => {
                      const reset = {};
                      csvHeaders.forEach(h => { reset[h] = null; });
                      setColumnMapping(reset);
                    }}
                    style={{ borderRadius: 8 }}
                  >
                    Reset mapping
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleImport}
                    loading={importing}
                    style={{ borderRadius: 8 }}
                  >
                    Confirm & Import
                  </Button>
                </Space>
              </div>
            );
          })() : (
            <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
              <Button type="primary" onClick={resetImportModal} style={{ borderRadius: 8 }}>Done</Button>
            </Space>
          )
        }
        destroyOnHidden
        width={importStep === 2 ? 760 : 480}
      >
        {/* ── Step 1: Upload + List ── */}
        {importStep === 1 && (
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Mailing List</Text>
              <Segmented
                block
                value={importListMode}
                onChange={v => { setImportListMode(v); setImportListId(''); setNewListName(''); }}
                options={[
                  { label: 'Select existing list', value: 'existing' },
                  { label: 'Create new list', value: 'new' },
                ]}
                style={{ marginBottom: 10 }}
              />
              {importListMode === 'existing' ? (
                <Select
                  placeholder="Choose a list…"
                  value={importListId || undefined}
                  onChange={setImportListId}
                  style={{ width: '100%' }}
                  size="large"
                  showSearch
                  optionFilterProp="children"
                >
                  {lists.map(l => (
                    <Option key={l.id} value={l.id}>
                      <Space>
                        <UnorderedListOutlined style={{ color: '#6366f1' }} />
                        {l.name}
                      </Space>
                    </Option>
                  ))}
                </Select>
              ) : (
                <Input
                  placeholder="e.g., Product Launch List"
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  size="large"
                  style={{ borderRadius: 10 }}
                  prefix={<PlusOutlined style={{ color: '#6366f1' }} />}
                  autoFocus
                />
              )}
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text strong>CSV File</Text>
                {csvFile && csvRowCount !== null && (
                  <Tag icon={<TeamOutlined />} color="green" style={{ borderRadius: 6, fontWeight: 600, margin: 0 }}>
                    {csvRowCount} row{csvRowCount !== 1 ? 's' : ''} detected
                  </Tag>
                )}
              </div>
              <Upload
                accept=".csv,.xlsx"
                beforeUpload={handleCsvSelect}
                onRemove={() => { setCsvFile(null); setCsvRowCount(null); }}
                maxCount={1}
                fileList={csvFile ? [{ uid: '1', name: csvFile.name, status: 'done' }] : []}
              >
                <Button icon={<UploadOutlined />} style={{ borderRadius: 10, width: '100%' }} size="large">
                  Select CSV file
                </Button>
              </Upload>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', gap: 12 }}>
              <div style={{ fontSize: 12, color: '#1d4ed8', lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Tip</div>
                <div style={{ color: '#3b82f6' }}>Supports .csv and .xlsx files. Map any column names on the next step.</div>
              </div>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                onClick={downloadCsvTemplate}
                style={{ borderRadius: 8, borderColor: '#93c5fd', color: '#2563eb', background: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Template
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: Column Mapping ── */}
        {importStep === 2 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: themeToken.colorBgLayout }}>
                    {['FILE HEADER', 'DATA', 'CONTACT ATTRIBUTE', ''].map((h, i) => (
                      <th key={i} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${themeToken.colorBorderSecondary}`, whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvHeaders.map((header, idx) => {
                    const mapped = columnMapping[header];
                    const samples = csvSampleRows.map(r => r[header]).filter(Boolean).slice(0, 3);
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${themeToken.colorBorderSecondary}` }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: themeToken.colorText, whiteSpace: 'nowrap', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {header}
                        </td>
                        <td style={{ padding: '10px 12px', color: themeToken.colorTextSecondary, maxWidth: 180 }}>
                          {samples.length > 0
                            ? samples.map((v, i) => <div key={i} style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</div>)
                            : <span style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: 12 }}>—</span>
                          }
                        </td>
                        <td style={{ padding: '10px 12px', minWidth: 180 }}>
                          <Select
                            value={mapped || '__skip__'}
                            onChange={val => setColumnMapping(prev => ({ ...prev, [header]: val === '__skip__' ? null : val }))}
                            style={{ width: '100%' }}
                            size="small"
                          >
                            <Option value="__skip__"><Text style={{ color: '#9ca3af', fontStyle: 'italic' }}>Do not import</Text></Option>
                            {dbFields.map(f => (
                              <Option key={f.value} value={f.value}>{f.label}</Option>
                            ))}
                          </Select>
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', width: 32 }}>
                          {mapped && mapped !== '__skip__'
                            ? <span style={{ color: '#9DC533', fontSize: 16, fontWeight: 700 }}>✓</span>
                            : <span style={{ color: '#d1d5db', fontSize: 16 }}>—</span>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!Object.values(columnMapping).some(v => v === 'email') && (
              <Alert
                type="warning"
                showIcon
                message="Map at least one column to Email to proceed"
                style={{ marginTop: 12, borderRadius: 8 }}
              />
            )}
          </div>
        )}

        {/* ── Step 3: Results ── */}
        {importStep === 3 && importResult && (
          <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Row gutter={12}>
              {[
                { label: 'Added', value: importResult.added, color: '#9DC533', bg: '#EEF6D0' },
                { label: 'Updated', value: importResult.updated, color: '#3b82f6', bg: '#dbeafe' },
                { label: 'Duplicates', value: importResult.skipped_duplicate, color: '#f59e0b', bg: '#fef3c7' },
                { label: 'Skipped', value: importResult.skipped_invalid, color: '#ef4444', bg: '#fee2e2' },
              ].map(({ label, value, color, bg }) => (
                <Col span={6} key={label}>
                  <div style={{ background: bg, borderRadius: 10, padding: '14px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 24, fontWeight: 700, color }}>{value}</div>
                    <div style={{ fontSize: 11, color, fontWeight: 600, marginTop: 2 }}>{label}</div>
                  </div>
                </Col>
              ))}
            </Row>
            <Text style={{ color: '#6b7280', fontSize: 13 }}>
              Processed <strong>{importResult.total}</strong> row{importResult.total !== 1 ? 's' : ''} total.
              {importResult.updated > 0 && ' Existing subscribers were updated with any new field values.'}
            </Text>
            {importResult.errors && importResult.errors.length > 0 && (
              <div>
                <Text strong style={{ fontSize: 13, color: '#ef4444' }}>
                  {importResult.errors.length} issue{importResult.errors.length !== 1 ? 's' : ''}
                </Text>
                <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 8, border: `1px solid #fecaca`, borderRadius: 8 }}>
                  {importResult.errors.map((e, i) => (
                    <div key={i} style={{ padding: '6px 12px', borderBottom: i < importResult.errors.length - 1 ? '1px solid #fecaca' : 'none', fontSize: 12, display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      <Tag color="error" style={{ borderRadius: 4, fontSize: 11, flexShrink: 0 }}>Row {e.row}</Tag>
                      <Text style={{ color: '#6b7280' }}>{e.email}</Text>
                      <Text style={{ color: '#ef4444', marginLeft: 'auto' }}>{e.reason}</Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Subscribers;
